import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ArrowLeft, Loader2, TrendingUp, Users, XCircle, AlertTriangle, MapPin, LineChart as LineIcon, RefreshCw, Target, DollarSign } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DualViewSwitcher } from "../components/DualViewSwitcher";
import { PassportScoreCard } from "../components/PassportScoreCard";
import { FundraiserReferralCard } from "../components/FundraiserReferralCard";
import TapTrackLogo from "../components/TapTrackLogo";
import BondAccountView from "../components/BondAccountView";

const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

export default function FundraiserRecords() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Relocated from Homepage (OCI V2.16): signups, pay, passport
  const [mySignups, setMySignups] = useState([]);
  const [loadingSignups, setLoadingSignups] = useState(false);
  const [showSignups, setShowSignups] = useState(false);
  const [myPayRecords, setMyPayRecords] = useState([]);
  const [loadingPay, setLoadingPay] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [loadingPayslip, setLoadingPayslip] = useState(false);

  const loadMySignups = async () => {
    setLoadingSignups(true);
    try { const res = await apiClient.get("/fundraiser/signups"); setMySignups(res.data || []); }
    catch (e) {} finally { setLoadingSignups(false); }
  };
  const loadMyPay = async () => {
    setLoadingPay(true);
    try { const res = await apiClient.get("/fundraiser/payroll"); setMyPayRecords(res.data || []); }
    catch (e) { setMyPayRecords([]); } finally { setLoadingPay(false); }
  };
  const loadPayslipDetail = async (recordId) => {
    setLoadingPayslip(true);
    try { const res = await apiClient.get(`/fundraiser/payroll/${recordId}`); setSelectedPayslip(res.data); }
    catch (e) { /* non-fatal */ } finally { setLoadingPayslip(false); }
  };

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiClient.get("/fundraiser/records");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load records");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const summary = data?.summary || {};
  const trend = data?.trend || [];
  const cancellations = data?.cancellations || [];
  const recentKnocks = data?.recentKnocks || [];

  const outcomeCls = (o) => ({
    converted: "bg-emerald-50 text-emerald-800 border-emerald-200",
    callback: "bg-indigo-50 text-indigo-800 border-indigo-200",
    not_interested: "bg-slate-50 text-slate-700 border-slate-200",
    no_answer: "bg-slate-50 text-slate-600 border-slate-200",
  }[o] || "bg-slate-50 text-slate-700 border-slate-200");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fundraiser-records-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} data-testid="records-back-btn" className="h-9 px-2">
            <ArrowLeft className="h-4 w-4 sm:mr-1"/><span className="hidden sm:inline">Back</span>
          </Button>
          <TapTrackLogo className="hidden sm:block h-8 w-auto"/>
          <div className="flex-1">
            <h1 className="font-heading font-bold text-lg sm:text-xl text-slate-900">My Records</h1>
            <p className="text-xs text-slate-500">Personal performance · signups · cancellations · knocks</p>
          </div>
          <Button variant="ghost" size="sm" onClick={load} className="h-9 px-2" data-testid="records-refresh-btn" disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>}
          </Button>
          <DualViewSwitcher />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {error && (
          <Card className="border-rose-200 bg-rose-50"><CardContent className="p-4 text-sm text-rose-800" data-testid="records-error">{error}</CardContent></Card>
        )}

        {/* OCI V2.16 — Fundraiser Passport (relocated from Homepage) */}
        <PassportScoreCard
          userId={user?.id}
          onCompletePassport={() => navigate("/fundraiser/passport")}
        />

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="records-summary">
          {[
            { k: "total", label: "Total Signups", value: summary.totalSignups ?? "—", sub: `${summary.last30dSignups ?? 0} in last 30d`, Icon: TrendingUp, cls: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-white", iconCls: "text-emerald-700" },
            { k: "active", label: "Active", value: summary.activeSignups ?? "—", sub: `${summary.failedPayments ?? 0} failed payments`, Icon: Users, cls: "border-slate-200 bg-white", iconCls: "text-slate-700" },
            { k: "cancel", label: "Cancellations", value: summary.cancellations ?? "—", sub: summary.totalSignups ? `${Math.round((summary.cancellations || 0) / summary.totalSignups * 100)}% cancel rate` : "—", Icon: XCircle, cls: "border-rose-200 bg-gradient-to-br from-rose-50 to-white", iconCls: "text-rose-700" },
            { k: "knocks", label: "Knocks / Conv", value: summary.totalKnocks ?? "—", sub: `${summary.conversionRatePct ?? 0}% conv rate · avg ${fmtGBP(summary.avgValueGBP)}`, Icon: MapPin, cls: "border-indigo-200 bg-gradient-to-br from-indigo-50 to-white", iconCls: "text-indigo-700" },
          ].map(({ k, label, value, sub, Icon, cls, iconCls }) => (
            <Card key={k} className={`p-3 border ${cls}`} data-testid={`records-tile-${k}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${iconCls}`}/>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{label}</div>
              </div>
              <div className="font-heading font-black text-2xl text-slate-900">{loading ? <Loader2 className="h-5 w-5 animate-spin"/> : value}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
            </Card>
          ))}
        </div>

        {summary.topRoad && (
          <Card className="border-indigo-200 bg-indigo-50"><CardContent className="p-3 text-sm text-indigo-900" data-testid="records-top-road">
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4"/><span className="font-semibold">Your top road:</span> {summary.topRoad.road} — {summary.topRoad.conversions} conversion(s).</div>
          </CardContent></Card>
        )}

        {/* Trend chart */}
        <Card className="border-slate-200 bg-white" data-testid="records-trend">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <LineIcon className="h-4 w-4 text-indigo-600"/>
              <h3 className="font-semibold text-slate-900 text-sm">Weekly Trend · last 12 weeks</h3>
            </div>
            {trend.length === 0 ? (
              <div className="text-xs text-slate-500 py-6 text-center">No weekly activity yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trend} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                  <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3"/>
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: "#64748b" }}/>
                  <YAxis tick={{ fontSize: 10, fill: "#64748b" }}/>
                  <Tooltip contentStyle={{ fontSize: 12 }}/>
                  <Line type="monotone" dataKey="signups" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Signups"/>
                  <Line type="monotone" dataKey="knocks" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} name="Knocks"/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Cancellations */}
          <Card className="border-slate-200 bg-white" data-testid="records-cancellations">
            <CardContent className="p-0">
              <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-rose-600"/>
                <h3 className="font-semibold text-slate-900 text-sm">My Cancellations</h3>
                <Badge className="bg-rose-50 text-rose-800 border-rose-200 border text-[10px] px-2 py-0">{cancellations.length}</Badge>
              </div>
              {cancellations.length === 0 ? (
                <div className="text-xs text-slate-500 py-6 text-center" data-testid="records-cancellations-empty">No cancellations on record. Strong retention.</div>
              ) : (
                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-[1]">
                      <TableRow className="bg-slate-50"><TableHead className="text-xs">Customer</TableHead><TableHead className="text-xs">Type</TableHead><TableHead className="text-xs text-right">Amount</TableHead><TableHead className="text-xs">Reason</TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {cancellations.map(c => (
                        <TableRow key={c.id} className="text-xs" data-testid={`cancellation-row-${c.id}`}>
                          <TableCell className="font-medium">{c.customerName}</TableCell>
                          <TableCell className="capitalize">{(c.type || "").replace(/_/g, " ")}</TableCell>
                          <TableCell className="text-right font-mono">{fmtGBP(c.amount)}</TableCell>
                          <TableCell className="text-[11px] text-slate-500">{c.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent knocks */}
          <Card className="border-slate-200 bg-white" data-testid="records-knocks">
            <CardContent className="p-0">
              <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-indigo-600"/>
                <h3 className="font-semibold text-slate-900 text-sm">Recent Knocks</h3>
                <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200 border text-[10px] px-2 py-0">{recentKnocks.length}</Badge>
              </div>
              {recentKnocks.length === 0 ? (
                <div className="text-xs text-slate-500 py-6 text-center" data-testid="records-knocks-empty">No knock history yet — log your first interaction in the Knock Sheet.</div>
              ) : (
                <div className="overflow-y-auto max-h-96 p-2 space-y-1">
                  {recentKnocks.map(k => (
                    <div key={k.id} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-100 text-xs" data-testid={`knock-row-${k.id}`}>
                      <div className="flex-1 min-w-0 truncate"><span className="font-medium text-slate-900">{k.road}</span>{k.houseNumber && <span className="text-slate-500"> · #{k.houseNumber}</span>}</div>
                      <Badge className={`border text-[10px] px-1.5 py-0 ${outcomeCls(k.outcome)}`}>{(k.outcome || "").replace(/_/g, " ")}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {summary.failedPayments > 0 && (
          <Card className="border-amber-300 bg-amber-50"><CardContent className="p-3 text-sm text-amber-900 flex items-center gap-2" data-testid="records-failed-alert">
            <AlertTriangle className="h-4 w-4 text-amber-700"/>
            <span>{summary.failedPayments} of your signups have failed payments. Speak with your manager to recover them.</span>
          </CardContent></Card>
        )}

        {/* OCI V2.16 — My Sign-Ups (relocated from Homepage) */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-emerald-600" />
                <h3 className="font-heading font-bold text-slate-900">My Sign-Ups</h3>
              </div>
              <Button variant="outline" size="sm"
                onClick={() => { setShowSignups(!showSignups); if (!showSignups && mySignups.length === 0) loadMySignups(); }}
                data-testid="toggle-signups-btn">
                {showSignups ? "Hide" : "View"}
              </Button>
            </div>
            {showSignups && (
              loadingSignups ? (
                <div className="text-center py-6 text-slate-400">Loading...</div>
              ) : mySignups.length === 0 ? (
                <div className="text-center py-6 text-slate-400" data-testid="no-signups">No sign-ups yet</div>
              ) : (
                <div className="overflow-x-auto" data-testid="signups-table">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-slate-500">
                        <th className="pb-2 pr-3">Engage Tracking ID</th>
                        <th className="pb-2 pr-3">Date</th>
                        <th className="pb-2 pr-3">Type</th>
                        <th className="pb-2 pr-3">Amount</th>
                        <th className="pb-2 pr-3">Age</th>
                        <th className="pb-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mySignups.map((s, i) => (
                        <tr key={s.taptractId || `signup-${i}`} className="border-b border-slate-100">
                          <td className="py-2 pr-3 font-mono text-xs">{s.taptractId}</td>
                          <td className="py-2 pr-3 text-slate-600">{s.date ? new Date(s.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—"}</td>
                          <td className="py-2 pr-3">{s.type}</td>
                          <td className="py-2 pr-3">£{Number(s.amount || 0).toFixed(2)}</td>
                          <td className="py-2 pr-3">{s.age || "—"}</td>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                              {s.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* OCI V2.40 — Refer a Fundraiser (fundraiser-to-fundraiser referral MVP) */}
        <FundraiserReferralCard />


        {/* OCI V2.16 — My Pay (relocated from Homepage) */}
        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                <h3 className="font-heading font-bold text-slate-900">My Pay</h3>
              </div>
              <Button variant="outline" size="sm"
                onClick={() => { setShowPay(!showPay); if (!showPay) loadMyPay(); }}
                data-testid="toggle-pay-btn">
                {showPay ? "Hide" : "View"}
              </Button>
            </div>
            {showPay && (
              loadingPay ? (
                <div className="text-center py-6 text-slate-400">Loading...</div>
              ) : myPayRecords.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-6">No payroll records yet</p>
              ) : (
                <div className="space-y-2" data-testid="my-pay-list">
                  {myPayRecords.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => loadPayslipDetail(r.id)} data-testid={`pay-record-${r.id}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{r.weekStart} to {r.weekEnd}</p>
                        <p className="text-xs text-slate-500">{(r.lineItems?.length ?? 0)} item{(r.lineItems?.length ?? 0) === 1 ? '' : 's'}{r.status === 'voided' ? ' · VOIDED' : ''}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-emerald-700">£{Number(r.payable ?? r.net ?? 0).toFixed(2)}</p>
                        <p className="text-xs text-slate-400">payable</p>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* Package 6F — read-only bond account (self only) */}
        <BondAccountView />
      </main>

      {/* Payslip Detail Modal (relocated from Homepage) */}
      <Dialog open={!!selectedPayslip} onOpenChange={() => setSelectedPayslip(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payslip: {selectedPayslip?.weekStart} to {selectedPayslip?.weekEnd}</DialogTitle>
          </DialogHeader>
          {loadingPayslip ? (
            <div className="text-center py-6 text-slate-400">Loading...</div>
          ) : selectedPayslip && (
            <div className="space-y-4 py-2" data-testid="payslip-detail">
              {selectedPayslip.status === 'voided' && (
                <div className="text-xs font-semibold text-red-600 bg-red-50 rounded px-2 py-1 text-center" data-testid="payslip-voided-badge">VOIDED</div>
              )}
              {/* Earnings line items — campaign-labelled, Regular Giving vs Lottery Lines */}
              <div>
                <h4 className="font-medium text-sm text-slate-700 mb-2">Earnings</h4>
                <div className="space-y-1" data-testid="payslip-line-items">
                  {(selectedPayslip.lineItems || []).map((li, i) => (
                    <div key={i} className="flex justify-between items-start text-sm p-2 bg-slate-50 rounded" data-testid={`payslip-line-item-${i}`}>
                      <div className="pr-2">
                        <p className="text-slate-800">{li.description}</p>
                        <p className="text-xs text-slate-400">{li.unitType === 'lottery_line' ? 'Lottery Lines' : 'Regular Giving Signup'} · {li.campaignName}</p>
                      </div>
                      <span className="font-medium whitespace-nowrap">£{Number(li.gross).toFixed(2)}</span>
                    </div>
                  ))}
                  {(!selectedPayslip.lineItems || selectedPayslip.lineItems.length === 0) && (
                    <p className="text-xs text-slate-400 py-2">No paid items in this period.</p>
                  )}
                </div>
              </div>
              {/* Pay summary */}
              <div className="space-y-1 text-sm">
                <div className="flex justify-between font-semibold border-t pt-1"><span>Gross Pay</span><span data-testid="payslip-gross">£{Number(selectedPayslip.gross ?? 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Bond Contribution ({Number(selectedPayslip.bondPercentage ?? 0)}%)</span><span className="text-amber-600" data-testid="payslip-bond-contribution">-£{Number(selectedPayslip.bondContribution ?? selectedPayslip.bond ?? 0).toFixed(2)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1"><span>Final Payable</span><span className="text-emerald-700" data-testid="payslip-payable">£{Number(selectedPayslip.payable ?? selectedPayslip.net ?? 0).toFixed(2)}</span></div>
              </div>
              {/* Bond account — cancellations affect this, never gross pay */}
              <div className="space-y-1 text-sm bg-amber-50/60 rounded-lg p-3" data-testid="payslip-bond-account">
                <h4 className="font-medium text-slate-700 mb-1">Bond Account</h4>
                <div className="flex justify-between"><span className="text-slate-500">Previous Balance</span><span data-testid="payslip-prev-bond">£{Number(selectedPayslip.previousBondBalance ?? 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">+ Contribution (this payslip)</span><span className="text-emerald-700">+£{Number(selectedPayslip.bondContribution ?? 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">− Cancellation Deductions</span><span className="text-red-600" data-testid="payslip-cancel-deductions">-£{Number(selectedPayslip.cancellationDeductions ?? 0).toFixed(2)}</span></div>
                <div className="flex justify-between font-bold border-t border-amber-200 pt-1"><span>New Bond Balance</span><span data-testid="payslip-new-bond">£{Number(selectedPayslip.newBondBalance ?? 0).toFixed(2)}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
